import { CommentsQueryDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import {
  PaginatedQueryConfig,
  PaginationParams,
  PaginationResponse,
  usePaginatedQuery,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';

import { api } from '@/lib/api-client';
import { CommentEntity } from '@/types/api';

export type GetConversationCommentsParams = CommentsQueryDto & PaginationParams;

export const getConversationComments = async (
  orgId: string,
  projectId: string,
  params: GetConversationCommentsParams,
): Promise<PaginationResponse<CommentEntity>> => {
  const { data } = await api.get<undefined, ApiResponse<PaginationResponse<CommentEntity>>>(
    `/orgs/${orgId}/projects/${projectId}/comments/conversations`,
    {
      params,
    },
  );

  return data;
};

export const getConversationCommentsQueryKey = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters?: CommentsQueryDto | null,
) => {
  const queryKey: unknown[] = ['comments', 'conversations'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (filters) queryKey.push('filters', filters);
  return queryKey;
};

export const getConversationCommentsQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters: CommentsQueryDto | undefined | null,
): UsePaginatedQueryOptions<CommentEntity> => {
  return {
    queryKey: getConversationCommentsQueryKey(orgId, projectId, filters),
    queryFn: ({ pageParam }) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getConversationComments(orgId!, projectId!, {
        ...filters,
        ...pageParam,
      }),
    enabled: !!orgId && !!projectId,
  };
};

interface UseConversationCommentsOptions {
  queryConfig?: PaginatedQueryConfig<typeof getConversationCommentsQueryOptions>;
}

export const useConversationComments = (
  filters?: CommentsQueryDto | null,
  { queryConfig }: UseConversationCommentsOptions = {},
) => {
  const { orgId, projectId } = useScopeIds();
  return usePaginatedQuery({
    ...getConversationCommentsQueryOptions(orgId, projectId, filters),
    ...queryConfig,
  });
};
